import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { NAV_KEYS, LOGO_SRC } from "@/mock";
import { useLanguage } from "@/i18n/LanguageContext";

export const Logo = ({ href = "#top" }: { href?: string }) => (
  <Link to={href === "#top" ? "/" : href} data-testid="navbar-brand-logo" className="flex items-center gap-3 group">
    <img src={LOGO_SRC} alt="PLAYS logo" className="w-14 h-14 object-contain" />
    <div className="leading-none text-center">
      <div className="font-archivo font-extrabold text-[22px] tracking-tight text-[#111]">PLAYS</div>
      <div className="text-[8px] font-semibold tracking-[0.18em] text-neutral-500 mt-0.5">PEOPLE · LINKED · AROUND · YOUR · SPORT</div>
    </div>
  </Link>
);

const Header = () => {
  const { lang, setLang, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const prefix = pathname === "/" ? "" : "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const LangSwitch = ({ mobile = false }: { mobile?: boolean }) => (
    <>
      {(["EN", "PT"] as const).map((code) => {
        const value = code.toLowerCase() as "en" | "pt";
        return (
          <button
            key={code}
            data-testid={`lang-switch-${value}${mobile ? "-mobile" : ""}`}
            onClick={() => setLang(value)}
            className={
              mobile
                ? `px-3 py-1.5 text-xs font-bold rounded border transition-colors ${lang === value ? "bg-[#111] text-white border-[#111]" : "text-neutral-600 border-neutral-300"}`
                : `px-2.5 py-1 text-xs font-bold rounded transition-colors ${lang === value ? "bg-[#111] text-white" : "text-neutral-600 hover:text-[#111]"}`
            }
          >
            {code}
          </button>
        );
      })}
    </>
  );

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[#F4F4F2]/90 backdrop-blur-md shadow-[0_1px_0_rgba(0,0,0,0.06)]" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1320px] mx-auto px-6 lg:px-8 h-[72px] flex items-center justify-between">
        <Logo />
        <nav className="hidden lg:flex items-center gap-8">
          {NAV_KEYS.map((l) => (
            <a
              key={l.key}
              href={`${prefix}${l.href}`}
              data-testid={`nav-link-${l.key}`}
              className="text-[15px] font-medium text-neutral-700 hover:text-[#2E4FE6] transition-colors duration-200"
            >
              {t.nav[l.key]}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center bg-white rounded-md p-0.5 border border-neutral-200">
            <LangSwitch />
          </div>
          <button
            data-testid="navbar-menu-toggle"
            className="lg:hidden flex flex-col gap-1.5 w-7 h-7 items-center justify-center"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            <span className={`block h-0.5 w-6 bg-[#111] transition-transform ${open ? "rotate-45 translate-y-2" : ""}`} />
            <span className={`block h-0.5 w-6 bg-[#111] transition-opacity ${open ? "opacity-0" : ""}`} />
            <span className={`block h-0.5 w-6 bg-[#111] transition-transform ${open ? "-rotate-45 -translate-y-2" : ""}`} />
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden bg-[#F4F4F2] border-t border-neutral-200 px-6 py-4" data-testid="navbar-mobile-menu">
          <nav className="flex flex-col gap-3">
            {NAV_KEYS.map((l) => (
              <a key={l.key} href={`${prefix}${l.href}`} onClick={() => setOpen(false)} className="text-base font-medium text-neutral-800 py-1.5">
                {t.nav[l.key]}
              </a>
            ))}
            <div className="flex items-center justify-center gap-2 pt-2">
              <LangSwitch mobile />
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
